import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, User, Menu, Search, X, ChevronDown } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { useSettings } from "../contexts/SettingsContext";
import LanguageSwitcher from "./LanguageSwitcher";
import api, { MEDIA } from "../lib/api";

export default function Header() {
    const navigate = useNavigate();
    const { user, logout, isAdmin, isStaff } = useAuth();
    const { itemCount, setOpen: setCartOpen } = useCart();
    const { settings } = useSettings();
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [categories, setCategories] = React.useState([]);
    const [search, setSearch] = React.useState("");
    const [shopOpen, setShopOpen] = React.useState(false);
    React.useEffect(() => { api.get("/categories").then(r => setCategories(r.data || [])).catch(() => {}); }, []);

    const topCategories = categories.filter(c => !c.parent_id);
    const [mobileShopOpen, setMobileShopOpen] = React.useState(false);
    const [searchBarVisible, setSearchBarVisible] = React.useState(true);
    const lastScrollY = React.useRef(0);

    React.useEffect(() => {
        const onScroll = () => {
            const y = window.scrollY;
            if (y > lastScrollY.current && y > 60) {
                setSearchBarVisible(false);   // scroll down → ascunde
            } else {
                setSearchBarVisible(true);    // scroll up sau top → arată
            }
            lastScrollY.current = y;
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const doSearch = (e) => {
        e.preventDefault();
        if (search.trim()) { navigate(`/magazin?q=${encodeURIComponent(search.trim())}`); setSearch(""); setMobileOpen(false); }
    };

    const navLink = ({ isActive }) =>
        `px-3 py-2 text-sm font-medium transition-colors ${isActive ? "text-primary" : "text-foreground hover:text-primary"}`;

    return (
        <header className="sticky top-0 z-40 bg-background/85 backdrop-blur-xl border-b border-border">
            <div className="hidden md:block border-b border-border/50 bg-muted/50">
                <div className="container-x flex items-center justify-between h-9 text-xs text-muted-foreground">
                    <span data-testid="top-bar-phone">☏ {settings.contact_phone || settings.company_phone || ""}</span>
                    <div className="flex items-center gap-4">
                        <LanguageSwitcher />
                        <Link to="/contact" className="hover:text-foreground" data-testid="top-contact-link">Contact</Link>
                    </div>
                </div>
            </div>

            <div className="container-x flex items-center justify-between h-20 gap-4">
                <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
                    {settings.logo_url ? (
                        <img src={settings.logo_url.startsWith("http") ? settings.logo_url : MEDIA(settings.logo_url)}
                             alt={settings.site_name || settings.company_name || "ccashop"}
                             className="h-10 max-w-[180px] object-contain" />
                    ) : (
                        <div className="leading-none">
                            <div className="font-heading font-bold text-xl tracking-tight">{settings.site_name || settings.company_name || "ccashop"}</div>
                            {(settings.tagline || settings.company_tagline) && (
                                <div className="text-[11px] text-muted-foreground">{settings.tagline || settings.company_tagline}</div>
                            )}
                        </div>
                    )}
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    <NavLink to="/" end className={navLink} data-testid="nav-home">Acasă</NavLink>
                    <div className="relative" onMouseEnter={() => setShopOpen(true)} onMouseLeave={() => setShopOpen(false)}>
                        <button className="px-3 py-2 text-sm font-medium text-foreground hover:text-primary flex items-center gap-1" data-testid="nav-shop">
                            Magazin <ChevronDown className="h-3 w-3" />
                        </button>
                        {shopOpen && (
                            <div className="absolute top-full left-0 pt-2 z-50" style={{minWidth: "420px"}}>
                                <div className="bg-card border border-border rounded-2xl shadow-xl overflow-hidden animate-fade-in" data-testid="nav-shop-menu">
                                    <div className="grid grid-cols-2 gap-0 divide-x divide-border/50">
                                        {topCategories.map(c => {
                                            const children = categories.filter(x => x.parent_id === c.category_id);
                                            return (
                                                <div key={c.category_id} className="p-4 hover:bg-muted/40 transition-colors group/col">
                                                    <Link
                                                        to={`/magazin/${c.slug}`}
                                                        className="font-semibold text-sm text-foreground group-hover/col:text-primary transition-colors block"
                                                        data-testid={`nav-category-${c.slug}`}
                                                    >
                                                        {c.name} <span className="text-primary opacity-0 group-hover/col:opacity-100 transition-opacity">→</span>
                                                    </Link>
                                                    {c.description && (
                                                        <p className="text-[11px] text-muted-foreground mt-0.5 mb-2 line-clamp-2 leading-relaxed">{c.description}</p>
                                                    )}
                                                    {!c.description && <div className="mb-2" />}
                                                    {children.length > 0 && (
                                                        <div className="space-y-1">
                                                            {children.map(sc => (
                                                                <Link
                                                                    key={sc.category_id}
                                                                    to={`/magazin/${sc.slug}`}
                                                                    className="block text-xs text-muted-foreground hover:text-primary transition-colors py-0.5"
                                                                    data-testid={`nav-subcategory-${sc.slug}`}
                                                                >
                                                                    {sc.name}
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="border-t border-border">
                                        <Link
                                            to="/magazin"
                                            className="flex items-center justify-center gap-2 px-4 py-3 bg-primary/5 hover:bg-primary/10 text-primary text-sm font-semibold transition-colors"
                                            data-testid="nav-all-products"
                                        >
                                            Toate produsele →
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <NavLink to="/pachete" className={navLink} data-testid="nav-packages">Pachete</NavLink>
                    <NavLink to="/servicii" className={navLink} data-testid="nav-services">Servicii</NavLink>
                    {settings.portfolio_enabled !== false && <NavLink to="/portofoliu" className={navLink} data-testid="nav-portfolio">Portofoliu</NavLink>}
                    {settings.blog_enabled !== false && <NavLink to="/blog" className={navLink} data-testid="nav-blog">Blog</NavLink>}
                    <NavLink to="/contact" className={navLink} data-testid="nav-contact">Contact</NavLink>
                </nav>

                <div className="flex items-center gap-2">
                    <form onSubmit={doSearch} className="hidden md:flex items-center rounded-xl border border-border bg-card focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
                        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Caută produse..."
                               className="px-4 py-2 bg-transparent outline-none w-48 text-sm rounded-l-xl" data-testid="header-search-input" />
                        <button type="submit" className="px-3 text-muted-foreground hover:text-primary" data-testid="header-search-btn"><Search className="h-4 w-4" /></button>
                    </form>
                    {user ? (
                        <div className="relative group">
                            <button className="btn-ghost flex items-center gap-2" data-testid="header-account-btn">
                                <User className="h-5 w-5" />
                                <span className="hidden md:inline text-sm">{user.name.split(" ")[0]}</span>
                            </button>
                            <div className="absolute right-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all w-56 z-50">
                                <div className="card-soft overflow-hidden">
                                    <Link to="/cont" className="block px-4 py-3 hover:bg-muted border-b border-border/50 text-sm" data-testid="menu-account">Contul meu</Link>
                                    <Link to="/cont?tab=orders" className="block px-4 py-3 hover:bg-muted border-b border-border/50 text-sm" data-testid="menu-orders">Comenzile mele</Link>
                                    <Link to="/cont?tab=appointments" className="block px-4 py-3 hover:bg-muted border-b border-border/50 text-sm" data-testid="menu-appointments">Programări</Link>
                                    <Link to="/cont?tab=wishlist" className="block px-4 py-3 hover:bg-muted border-b border-border/50 text-sm" data-testid="menu-wishlist">Favorite</Link>
                                    {isAdmin && <Link to="/admin" className="block px-4 py-3 hover:bg-muted border-b border-border/50 text-sm text-primary font-medium" data-testid="menu-admin">Panou administrare</Link>}
                                    {!isAdmin && isStaff && user.role === "sales" && <Link to="/admin" className="block px-4 py-3 hover:bg-muted border-b border-border/50 text-sm text-primary font-medium" data-testid="menu-staff-panel">Panou Sales</Link>}
                                    {!isAdmin && isStaff && user.role === "installer" && <Link to="/admin/tickets" className="block px-4 py-3 hover:bg-muted border-b border-border/50 text-sm text-primary font-medium" data-testid="menu-staff-panel">Tickete & instalări</Link>}
                                    <button onClick={logout} className="w-full text-left px-4 py-3 hover:bg-muted text-sm text-destructive" data-testid="menu-logout">Deconectare</button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <Link to="/login" className="btn-ghost" data-testid="header-login-btn"><User className="h-5 w-5" /></Link>
                    )}
                    <button onClick={() => setCartOpen(true)} className="btn-ghost relative" data-testid="header-cart-btn">
                        <ShoppingCart className="h-5 w-5" />
                        {itemCount > 0 && (
                            <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[10px] w-5 h-5 flex items-center justify-center font-bold rounded-full" data-testid="cart-count">{itemCount}</span>
                        )}
                    </button>
                    <button className="lg:hidden btn-ghost" onClick={() => setMobileOpen(!mobileOpen)} data-testid="header-mobile-toggle">
                        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {/* Mobile search bar — hides on scroll down, shows on scroll up */}
            <div className={`md:hidden border-border/50 bg-background px-4 overflow-hidden transition-all duration-300 ease-in-out ${searchBarVisible ? "border-t py-2 max-h-16" : "max-h-0 py-0 border-0"}`} data-testid="mobile-search-bar">
                <form onSubmit={doSearch} className="flex items-center rounded-xl border-2 border-primary/40 bg-card focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
                    <Search className="h-4 w-4 text-muted-foreground ml-3 shrink-0" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Caută produse..."
                           className="px-3 py-2.5 bg-transparent outline-none flex-1 text-sm" data-testid="mobile-search-input" />
                    <button type="submit" className="px-3 text-primary font-semibold text-sm" data-testid="mobile-search-btn">→</button>
                </form>
            </div>

            {/* Hamburger menu */}
            {mobileOpen && (
                <div className="lg:hidden border-t border-border bg-background" data-testid="mobile-nav">
                    <div className="container-x py-4 space-y-0.5">
                        <NavLink to="/" end onClick={() => setMobileOpen(false)} className="block py-3 border-b border-border/50 text-sm font-medium" data-testid="mobile-nav-acasă">Acasă</NavLink>

                        {/* Magazin expandable */}
                        <div className="border-b border-border/50">
                            <button
                                onClick={() => setMobileShopOpen(o => !o)}
                                className="w-full flex items-center justify-between py-3 text-sm font-medium"
                                data-testid="mobile-nav-magazin-toggle"
                            >
                                <span>Magazin</span>
                                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${mobileShopOpen ? "rotate-180" : ""}`} />
                            </button>
                            {mobileShopOpen && (
                                <div className="pb-3 space-y-3">
                                    <Link
                                        to="/magazin"
                                        onClick={() => setMobileOpen(false)}
                                        className="block text-xs text-primary font-semibold px-2 py-1.5 rounded-lg bg-primary/5 hover:bg-primary/10 transition-colors"
                                    >
                                        ← Toate produsele
                                    </Link>
                                    {topCategories.map(cat => {
                                        const children = categories.filter(c => c.parent_id === cat.category_id);
                                        return (
                                            <div key={cat.category_id}>
                                                <Link
                                                    to={`/magazin/${cat.slug}`}
                                                    onClick={() => setMobileOpen(false)}
                                                    className="block text-sm font-semibold px-2 py-1 hover:text-primary transition-colors"
                                                    data-testid={`mobile-cat-${cat.slug}`}
                                                >
                                                    {cat.name}
                                                </Link>
                                                {children.length > 0 && (
                                                    <div className="flex flex-wrap gap-1.5 mt-1 pl-2">
                                                        {children.map(sc => (
                                                            <Link
                                                                key={sc.category_id}
                                                                to={`/magazin/${sc.slug}`}
                                                                onClick={() => setMobileOpen(false)}
                                                                className="px-2.5 py-1 rounded-full text-[11px] border border-border bg-muted hover:border-primary hover:text-primary transition-colors"
                                                                data-testid={`mobile-subcat-${sc.slug}`}
                                                            >
                                                                {sc.name}
                                                            </Link>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {[
                            ["Pachete", "/pachete"],
                            ["Servicii", "/servicii"],
                            settings.portfolio_enabled !== false && ["Portofoliu", "/portofoliu"],
                            settings.blog_enabled !== false && ["Blog", "/blog"],
                            ["Contact", "/contact"],
                        ].filter(Boolean).map(([label, path]) =>
                            <NavLink key={path} to={path} onClick={() => setMobileOpen(false)} className="block py-3 border-b border-border/50 text-sm font-medium" data-testid={`mobile-nav-${label.toLowerCase()}`}>{label}</NavLink>
                        )}

                        {/* Account shortcuts on mobile */}
                        <div className="pt-3 mt-2 border-t-2 border-border/70 space-y-0.5">
                            {user ? (
                                <>
                                    <Link to="/cont" onClick={() => setMobileOpen(false)} className="block py-2.5 text-sm text-muted-foreground hover:text-foreground">Contul meu</Link>
                                    <Link to="/cont?tab=orders" onClick={() => setMobileOpen(false)} className="block py-2.5 text-sm text-muted-foreground hover:text-foreground">Comenzile mele</Link>
                                    {isAdmin && <Link to="/admin" onClick={() => setMobileOpen(false)} className="block py-2.5 text-sm text-primary font-medium">Administrare</Link>}
                                    <button onClick={() => { logout(); setMobileOpen(false); }} className="block py-2.5 text-sm text-destructive hover:text-destructive/80">Deconectare</button>
                                </>
                            ) : (
                                <Link to="/login" onClick={() => setMobileOpen(false)} className="block py-2.5 text-sm text-primary font-medium">Autentificare / Înregistrare</Link>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </header>
    );
}
